import { Component } from '@angular/core';
import { Router } from '@angular/router';

import { createCsv } from '@fitogest/reactive-form-builder/csv';
import { FormType } from '@fitogest/reactive-form-builder/interfaces';
import { ColInterface } from '../../../../common/components/common-list-table/col.interface';
import { ColStylesInterface } from '../../../../common/components/common-list-table/col-styles.interface';
import { Filtering } from '../../../../service/filtering/filtering';
import { RequestHandler } from '../../../../service/OffService/request-handler';
import { Utils } from '../../../../common/utils';
import { StorageManager } from '../../../../common/storage-manager.class';
import { TareasApiService } from '../../../../service/api/tareas-api.service';
import { DashboardService } from '../../../../service/events/dashboard.service';
import { BaseView } from '../../../base-view';
import { environment } from '../../../../../environments/environment';
import { EcorregimenModel } from '../../../../models/tareas/ecorregimen.model';
import { EcorregimenesFormDefinition } from './ecorregimenes-form/form-control/form-definition';
import { TreeNode } from 'primeng/api';

@Component({
    selector: 'app-ecorregimenes',
    standalone: false,
    templateUrl: './ecorregimenes.component.html',
    styleUrls: ['./ecorregimenes.component.scss']
})
export class EcorregimenesComponent extends BaseView<EcorregimenModel> {
    public readonly showTareasBioExportCsv = environment.features.showTareasBioExportCsv;
    public readonly appName = environment.appName;
    public readonly serverUrl = environment.serverUrl;
    public readonly rolUser = StorageManager.getUser().rol;
    public readonly fincaName = environment.features.fincaName;
    public readonly sectorName = environment.features.sectorName;
    public readonly parcelaName = environment.features.parcelaName;

    public form: EcorregimenesFormDefinition | null = null;
    public getRequest!: RequestHandler<EcorregimenModel[]>;
    public deleteRequest!: RequestHandler<EcorregimenModel>;
    public override filtering: Filtering<EcorregimenModel> = new Filtering<EcorregimenModel>();
    public override canShow: Record<string, boolean> = {};
    public override model: EcorregimenModel = {};

    public pathToForm = 'tareas-crear-ecorregimen';
    public pathToEdit = 'tareas-editar-ecorregimen';
    public pathToDuplicate = 'tareas-duplicar-ecorregimen';

    public cols: ColInterface[] = [
        { field: 'parcelas', header: this.parcelaName },
        { field: 'fecha_entrada', header: 'Entrada Agua' },
        { field: 'fecha_salida', header: 'Salida Agua' },
        { field: 'fecha_seca_cultivo_agua', header: 'Inicio Inundación' },
        { field: 'fecha_fin_inundacion_invernal', header: 'Fin Inundación' },
        { field: 'fecha_seca_arroz', header: 'Seca Arroz' },
        { field: 'fecha_recoleccion_rastrojo', header: 'Recolección' },
        { field: 'operacion_paja', header: 'Operación' },
        { field: 'fecha_operacion', header: 'Fecha Operación' },
        { field: 'hora_inicio', header: 'Inicio Quema' },
        { field: 'hora_fin', header: 'Fin Quema' },
    ];

    public colsAva: ColInterface[] = [
        { field: 'finca', header: this.fincaName },
        { field: 'sectores', header: this.sectorName },
        { field: 'parcelas', header: this.parcelaName },
        { field: 'fecha_siembra', header: 'Fecha Siembra' },
        { field: 'dosis_siembra', header: 'Dosis Siembra' },
        { field: 'fecha_recoleccion_arroz', header: 'Fecha Recolección' },
        { field: 'fecha_nivelacion', header: 'Fecha Nivelación' },
        { field: 'fecha_malashierbas', header: 'Fecha Malas Hierbas' },
        { field: 'tareas_malashierbas', header: 'Tareas Malas Hierbas' },
        { field: 'control_mecanico_vn', header: 'Control vegetación palustre' }
    ];

    constructor(
        public tareasApi: TareasApiService,
        dashboard: DashboardService,
        private router: Router,
    ) {
        super(
            dashboard,
            tareasApi.ecorregimenes.GET,
            tareasApi.ecorregimenes.DELETE,
        );

        this.getRequest = tareasApi.ecorregimenes.GET;
        this.deleteRequest = tareasApi.ecorregimenes.DELETE;
    }

    public add(): void {
        this.router.navigate(['dashboard', this.pathToForm]);
    }

    public edit(data: EcorregimenModel): void {
        this.router.navigate(['dashboard', this.pathToEdit, data.id]);
    }

    public duplicate(data: EcorregimenModel): void {
        this.router.navigate(['dashboard', this.pathToDuplicate, data.id]);
    }

    public override show(modal: string, data: EcorregimenModel): void {
        super.show(modal, data);

        const formType = modal === 'delete' ? +FormType.DELETION : +FormType.INFORMATIVE;

        this.form = new EcorregimenesFormDefinition(formType, data);
    }

    public override hide(modal: string): void {
        super.hide(modal);
        this.form = null;
    }

    public globalConditionalStyle(_value: string, _col: ColInterface): ColStylesInterface {
        return { textAlign: 'center' };
    }

    public downloadCsv(): void {
        this.form = new EcorregimenesFormDefinition(+FormType.EXPORT, {});

        const csvContent = createCsv(
            this.form.fieldNames,
            this.form.fieldLabels,
            this.filtering.value as EcorregimenModel[],
        );
        const csvLink = document.getElementById('csvLink') as HTMLAnchorElement | null;

        if (!csvLink) {
            return;
        }

        csvLink.download = 'Tareas ecorregimenes.csv';
        csvLink.href = encodeURI(csvContent);
        csvLink.click();
    }

    public globalVisualTransform(value: string, col: ColInterface, row: TreeNode<EcorregimenModel>): string {
        const ecorregimen = row.data as EcorregimenModel;

        if (col.field === 'control_mecanico_vn') {
            return this.isTruthy(ecorregimen.control_mecanico_vn) ? 'Sí' : 'No';
        }

        if (col.field === 'dosis_siembra' && Utils.isNumber(value)) {
            return Utils.decimalFormat(value);
        }

        if (col.field === 'fecha_nivelacion') {
            return ecorregimen.tareas_malashierbas === 'Nivelación' ? String(ecorregimen.fecha_malashierbas ?? '') : '';
        }

        if (col.field === 'fecha_malashierbas') {
            return ecorregimen.tareas_malashierbas === 'Nivelación' ? '' : value;
        }

        if (Utils.isNumber(value) && col.field !== 'finca') {
            return Utils.decimalFormat(value, 2, '.', ',', 3);
        }

        if (col.field === 'parcelas') {
            const str = Utils.replaceAll(Utils.replaceAll(String(ecorregimen[col.field] || ''), '- ', ''), '\n', '\n, ');
            const parcelas = str.substring(0, str.length - 2);

            return parcelas.length > 50 ? parcelas.slice(0, 49) + '…' : parcelas;
        }

        if (col.field === 'sectores_nombres') {
            const str = Utils.replaceAll(Utils.replaceAll(String(ecorregimen[col.field] || ''), '- ', ''), '\n', '\n, ');

            return str.substring(0, str.length - 2);
        }

        return value;
    }

    private isTruthy(value: unknown): boolean {
        return value === true || value === 1 || value === '1' || value === 'true';
    }
}
